"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocalStorage } from "react-use";
const INTERCOM_SCRIPT_ID = "intercom-widget-script";
function useToken(userId, onSuccess) {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["intercom-token"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== onSuccess || $[2] !== userId) {
    t1 = async () => {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/intercom/token${userId ? `?userId=${userId}` : ""}`);
      if (!response.ok) {
        throw new Error("Failed to fetch intercom token");
      }
      const data = (await response.json()) as {
        token: string;
        userId: string;
      };
      if (!data.token) {
        throw new Error("Token is missing");
      }
      onSuccess?.(data.userId);
      return {
        token: data.token,
        userId: data.userId
      };
    };
    $[1] = onSuccess;
    $[2] = userId;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = userId !== undefined;
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useQuery(t3);
}

/**
 * Renders the Intercom messenger widget.
 * Loads the widget only after we have a JWT so it boots once as the authenticated user.
 * For local dev: add 127.0.0.1 (and port if required) to Intercom's allowed domains
 * (Settings → Installation → Where your messenger is allowed to appear).
 */
export function IntercomWidget() {
  const $ = _c(4);
  const appId = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;
  const [userId, setUserId] = useLocalStorage("intercom-user-id", null);
  const scriptLoaded = useRef(false);
  const {
    data
  } = useToken(userId, setUserId);
  let t0;
  if ($[0] !== data) {
    t0 = () => {
      if (!data?.token || !appId || typeof appId !== "string" || appId.trim() === "" || typeof window === "undefined") {
        return;
      }
      if (scriptLoaded.current || document.getElementById(INTERCOM_SCRIPT_ID)) {
        return;
      }
      scriptLoaded.current = true;
      window.intercomSettings = {
        app_id: appId,
        intercom_user_jwt: data.token,
        custom_launcher_selector: "#intercom-launcher-custom-launcher",
        hide_default_launcher: true
      };
      const script = document.createElement("script");
      script.id = INTERCOM_SCRIPT_ID;
      script.async = true;
      script.src = `https://widget.intercom.io/widget/${appId}`;
      document.head.appendChild(script);
    };
    $[0] = data;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = data?.token;
  let t2;
  if ($[2] !== t1) {
    t2 = [t1, appId];
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t0, t2);
  if (!appId || typeof appId !== "string" || appId.trim() === "") {
    return null;
  }
  return null;
}